import React from "react";

import {
  LineChart,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  Bar
} from "recharts";

import { AnalyticsCard } from "../AnalyticsCard";
import { HollowTable, HollowTableRow } from "../HollowTable";
import { Icon } from "../../atoms/Icon";

import useReportingVideo from "./ReportingVideoHooks";
import styles from "./ReportingOverview.module.scss";
import FormatHelpers from "./FormatHelpers";

const ReportingVideo = ({ videoReport, dateRange }) => {
  const {
    totalViewsGraphData,
    totalViewsColor,
    viewDurationGraphData,
    viewDurationColor,
    pinkButtonClicks,
    pinkButtonColor,
    totalSalesGraphData,
    totalSalesColor,
    videoOriginListTableData
  } = useReportingVideo(videoReport, dateRange);

  return (
    <div className={styles.reportingOverviewContainer}>
      <div className={styles.analyticsCardsRow}>
        <AnalyticsCard title="Total Views">
          <LineChart width={580} height={285} data={totalViewsGraphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              type="category"
              tickFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({
                  unixTime,
                  dateRange,
                  format: "MMM YY"
                })
              }
            />
            <YAxis type="number" />
            <Tooltip
              labelFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({ unixTime, dateRange })
              }
            />
            <Line
              type="monotone"
              dataKey="viewCount"
              stroke={totalViewsColor}
              name="# of views"
            />
          </LineChart>
        </AnalyticsCard>
        <AnalyticsCard title="Average Duration of Views">
          <LineChart width={580} height={285} data={viewDurationGraphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="duration"
              type="number"
              domain={["auto", "auto"]}
              scale="time"
              tickFormatter={FormatHelpers.durationFormatter}
            />
            <YAxis type="number" allowDecimals={false} />
            <Tooltip labelFormatter={FormatHelpers.durationFormatter} />
            <Line
              type="monotone"
              dataKey="viewerCount"
              name="# of viewers"
              stroke={viewDurationColor}
            />
          </LineChart>
        </AnalyticsCard>
      </div>
      <div className={styles.analyticsCardsRow}>
        <AnalyticsCard title="Purchase Intent Clicks">
          <BarChart width={580} height={285} data={pinkButtonClicks}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="duration"
              type="number"
              allowDecimals={false}
              domain={[0, "auto"]}
              tickFormatter={FormatHelpers.durationFormatter}
            />
            <YAxis type="number" allowDecimals={false} />
            <Tooltip labelFormatter={FormatHelpers.durationFormatter} />
            <Bar
              dataKey="count"
              fill={pinkButtonColor}
              name="# of clicks"
              barSize={4}
            />
          </BarChart>
        </AnalyticsCard>
        <AnalyticsCard title="Total Sales">
          <LineChart width={580} height={285} data={totalSalesGraphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              type="number"
              domain={["auto", "auto"]}
              scale="time"
              tickFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({
                  unixTime,
                  dateRange,
                  format: "MMM YY"
                })
              }
            />
            <YAxis
              type="number"
              tickFormatter={(amount) => `$${amount.toFixed(2)}`}
            />
            <Tooltip
              formatter={(amount) => `$${amount.toFixed(2)}`}
              labelFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({ unixTime, dateRange })
              }
            />
            <Line
              type="monotone"
              dataKey="salesAmount"
              name="$ in sales"
              stroke={totalSalesColor}
            />
          </LineChart>
        </AnalyticsCard>
      </div>
      <div className={styles.analyticsCardsRow}>
        <AnalyticsCard title="Video Origins List">
          <div className={styles.tableCardContainer}>
            <HollowTable titles={["Views", "Video Origin"]}>
              {videoOriginListTableData.map(({ id, views, origin }) => (
                <HollowTableRow key={id}>
                  <span className={styles.darkTableText}>
                    <Icon name="eye-open" color="#506883" size={15} />
                    {views}
                  </span>
                  {origin === "Other" ? (
                    <p>Other</p>
                  ) : (
                    <a
                      href={
                        origin.startsWith("https://") ||
                        origin.startsWith("http://")
                          ? origin
                          : `https://${origin}`
                      }
                      target="_blank"
                    >
                      {origin}
                    </a>
                  )}
                </HollowTableRow>
              ))}
            </HollowTable>
          </div>
        </AnalyticsCard>
      </div>
    </div>
  );
};

export default ReportingVideo;
